import React from "react";
import { useSettings } from "@/context/SettingsContext";
import { usePageContent } from "@/hooks/usePageContent";
import { MessageCircle, Watch, Layers, Circle, Square, CornerDownRight, Link2, Package } from "lucide-react";

const parts = [
  { icon: <Circle size={18} />, name: "Dial", desc: "Color, textura, índices y acabado." },
  { icon: <CornerDownRight size={18} />, name: "Agujas", desc: "Estilo, color y lume." },
  { icon: <Watch size={18} />, name: "Movimiento", desc: "NH35, NH36, VK63 y más." },
  { icon: <Square size={18} />, name: "Caja", desc: "Tamaño, material y acabado." },
  { icon: <Layers size={18} />, name: "Bisel", desc: "Cerámico, GMT, fluted o personalizado." },
  { icon: <Link2 size={18} />, name: "Correa", desc: "Acero, cuero, caucho o tela." },
  { icon: <Package size={18} />, name: "Estuche", desc: "Caja de presentación y empaque del reloj." },
];

const ConfiguratorPage = () => {
  const { settings } = useSettings();
  const c = usePageContent("configurator");
  const wa = (settings?.whatsapp_number || "").replace(/[^\d]/g, "");
  const waLink = `https://wa.me/${wa}?text=${encodeURIComponent(
    "Hola AQ Watches, quiero crear un reloj totalmente personalizado."
  )}`;

  return (
    <div data-testid="configurator-page" className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-14 py-20 lg:py-28">
      <div className="text-center mb-16">
        <span data-testid="coming-soon-badge" className="inline-block text-[10px] uppercase tracking-[0.3em] border border-black px-4 py-1.5 mb-6">
          {c.badge || "Próximamente"}
        </span>
        <p className="overline mb-4">{c.overline || "Configurador AQ Watches"}</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6">
          {c.title || "Diseña tu reloj."}
        </h1>
        <p className="text-[var(--aq-muted)] leading-relaxed max-w-2xl mx-auto">
          {c.subtitle || "Estamos construyendo un configurador interactivo donde podrás elegir cada componente de tu reloj."}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
        {parts.map((p) => (
          <div
            key={p.name}
            data-testid={`config-part-${p.name.toLowerCase()}`}
            className="border border-[var(--aq-border)] p-7 hover:border-black transition-colors"
          >
            <div className="flex items-center gap-3 mb-3 text-[var(--aq-ink)]">
              {p.icon}
              <h3 className="font-display text-xl">{p.name}</h3>
            </div>
            <p className="text-sm text-[var(--aq-muted)] leading-relaxed">{p.desc}</p>
            <span className="inline-block mt-5 text-[10px] uppercase tracking-[0.28em] text-[var(--aq-muted)]">
              Configurable pronto
            </span>
          </div>
        ))}
      </div>

      <div className="bg-[var(--aq-ink)] text-white px-6 py-16 lg:py-20 text-center">
        <p className="overline text-white/70 mb-4">{c.cta_overline || "Mientras tanto"}</p>
        <h2 className="font-display text-3xl sm:text-4xl mb-6">{c.cta_title || "Inicia tu diseño con nosotros."}</h2>
        <p className="text-white/75 mb-10 leading-relaxed max-w-xl mx-auto">
          {c.cta_text || "Envíanos un mensaje y co-diseñaremos tu reloj completamente a medida."}
        </p>
        <a
          href={waLink}
          target="_blank"
          rel="noreferrer"
          data-testid="configurator-whatsapp-cta"
          className="btn-whatsapp"
        >
          <MessageCircle size={16} /> {c.cta_button || "Crear mi Reloj por WhatsApp"}
        </a>
      </div>
    </div>
  );
};

export default ConfiguratorPage;
